<template>
  <div id="profile" class="profile">
    <h2 class="page-heading">Your Profile</h2>
    <div>
      <form class="form" @submit.prevent="updateUserProfile" @keydown.enter.prevent>
        <div class="form-row-wrapper">
          <!-- <div class="form-row">
            <label for="userId">User ID</label>
            <input
              id="userId"
              type="text"
              v-model="userId"
              required
              readonly
              @click="onUserIdClick"
              class="input input--userid"
              title="Copy to clipboard"
            />
          </div> -->
          <div class="form-row">
            <label for="firstName">First name</label>
            <input id="firstName" type="text" v-model="firstName" @input="firstNameInput" minlength="2" maxlength="16" ref="firstName" autocomplete="given-name" />
          </div>
          <div class="form-row">
            <label for="lastName">Last name</label>
            <input id="lastName" type="text" v-model="lastName" @input="lastNameInput" minlength="2" maxlength="16" autocomplete="family-name" />
          </div>
          <div class="form-row">
            <label for="gamerTag">Gamertag / Display name <span class="required-asterisk" aria-hidden="true" title="Required">*</span></label>
            <input id="gamerTag" type="text" v-model="gamerTag" @input="gamerTagInput" :maxlength="gamerTagMaxLength" required />
          </div>
          <div class="form-row">
            <label for="email">Email <span class="required-asterisk" aria-hidden="true" title="Required">*</span></label>
            <input id="email" type="email" class="email-input" v-model="getEmail" required readonly />
          </div>
          <div class="form-row select">
            <label for="yearBorn">Year born</label>
            <select id="yearBorn" v-model="yearBorn">
              <option v-for="year in years" :value="year" :key="year">
                {{ year }}
              </option>
            </select>
            <div class="select__arrow"></div>
          </div>
          <div class="form-row select">
            <label for="country">Country</label>
            <select id="country" v-model="country">
              <option value="" disabled>Please choose</option>
              <option value="GB">United Kingdom</option>
              <option value="US">United States</option>
              <option value="" disabled></option>
              <option v-for="country in countries" :value="country.code" :key="country.code">
                {{ country.name }}
              </option>
            </select>
            <div class="select__arrow"></div>
          </div>
          <div class="form-row">
            <label class="control control--checkbox"
              >Interface sounds
              <input type="checkbox" v-model="playSoundEffects" />
              <div class="control__indicator"></div>
            </label>
          </div>
          <div class="form-row">
            <label class="control control--checkbox"
              >Override OS dark mode
              <input type="checkbox" v-model="overrideOsDarkMode" @change="onOverrideOsDarkModeChange" />
              <div class="control__indicator"></div>
            </label>
          </div>
          <div v-if="overrideOsDarkMode" class="form-row select">
            <label for="prefersColorScheme">Prefers color scheme</label>
            <select id="prefersColorScheme" v-model="prefersColorScheme" :disabled="!overrideOsDarkMode" @change="onPrefersColorSchemeChange">
              <option v-for="colorScheme in colorSchemesSelectData" :value="colorScheme.value" :key="colorScheme.value">
                {{ colorScheme.label }}
              </option>
            </select>
            <div class="select__arrow"></div>
          </div>
          <div class="form-row select">
            <label for="avatarToUse">Profile picture</label>
            <select id="avatarToUse" v-model="avatarToUse" @change="onAvatarToUseChange">
              <option v-for="avatar in avatarSelectData" :value="avatar.value" :key="avatar.value">
                {{ avatar.label }}
              </option>
            </select>
            <div class="select__arrow"></div>
          </div>
          <div v-if="avatarToUse === avatars.DEFAULT" class="form-row">
            <img :src="defaultAvatarUrl" alt="Default avatar image" width="240" height="240" class="default-avatar-image" />
          </div>
          <div v-else-if="avatarToUse === avatars.GRAVATAR" class="form-row">
            <div class="gravatar-wrapper">
              <v-gravatar :hash="hash" :size="240" default-img="mp" rating="g" alt="Gravatar image" class="gravatar-image" />
              <span>
                <a href="https://en.gravatar.com/" target="_blank">Edit</a>
              </span>
            </div>
          </div>
          <div v-else-if="avatarToUse === avatars.CUSTOM" class="form-row">
            <div class="custom-avatar-wrapper">
              <i v-if="showCustomAvatarLoader" class="loader loader--light">Loading&hellip;</i>
              <img v-if="customAvatarUrl" :src="resizedCustomAvatarUrl" alt="Custom avatar image" class="custom-avatar-image" :class="customAvatarOrientation.toLowerCase()" />
              <div>
                <span v-if="customAvatarFilename">Change</span>
                <span v-if="!customAvatarFilename">Upload</span>
                <input type="file" id="avatar" name="avatar" accept="image/png, image/jpeg" @change="readImageData" />
                <progress v-if="imageUploadPercent > 0 && imageUploadPercent < 100" :value="imageUploadPercent" max="100">{{ imageUploadPercent }}%</progress>
              </div>
            </div>
          </div>
        </div>
        <button type="submit">Update profile</button>
        <button type="button" @click="onCancel" class="button button--as-link cancel-btn">Cancel</button>
      </form>
    </div>
  </div>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import firebase from 'firebase/app';
import 'firebase/storage';
import md5 from 'md5';

import capitalize from './../helpers/capitalize';
import logUserActivity from './../helpers/logUserActivity';
import updateUserDataInFirebase from './../helpers/updateUserDataInFirebase';
import {VIEWED_PROFILE, UPDATED_PROFILE} from './../constants/activityLogs';
import getFormattedTimestamp from './../helpers/getFormattedTimestamp';
import bytesToSize from './../helpers/bytesToSize';
import getOrientationFromDimensions from './../helpers/getOrientationFromDimensions';
import {COUNTRIES} from './../constants/countries';
import resizedCustomAvatarUrl from './../helpers/resizedCustomAvatarUrl';
import {
  AVATAR_SELECT_DATA,
  AVATARS,
  COLOR_SCHEMES,
  COLOR_SCHEMES_SELECT_DATA,
  FIREBASE_STORAGE_RESIZED_IMAGE_SUFFIXES,
  FIREBASE_STORAGE_USER_AVATARS_FOLDER,
  GAMERTAG_MAX_LENGTH,
  MAXIMUM_CUSTOM_AVATAR_UPLOAD_BYTES,
  NOTIFICATION_SETTINGS,
  ORIENTATIONS,
  PAGE_TITLE_APP_NAME,
} from './../constants/globalConstants';

const userVuexModule = createNamespacedHelpers('userVuexModule');

export default {
  name: 'profile',
  title: 'Profile',
  components: {},
  data: function() {
    return {
      avatars: AVATARS,
      avatarSelectData: AVATAR_SELECT_DATA,
      avatarToUse: '',
      colorSchemesSelectData: COLOR_SCHEMES_SELECT_DATA,
      countries: COUNTRIES,
      country: '',
      customAvatarFilename: '',
      customAvatarOrientation: '',
      customAvatarOriginalFilesizeKb: 0,
      customAvatarOriginalHeight: 0,
      customAvatarOriginalWidth: 0,
      customAvatarUrl: '',
      dataThemeBeforeEditing: '',
      firstName: '',
      gamerTag: '',
      gamerTagMaxChars: 0,
      imageUploadPercent: 0,
      lastName: '',
      overrideOsDarkMode: null,
      playSoundEffects: null,
      prefersColorScheme: '',
      submittingForm: false,
      userId: '',
      userIdShort: '',
      userSavedChanges: false,
      yearBorn: '',
      years: [],
      uploadedCustomAvatarImage: {
        extension: null,
        height: null,
        isReadingFile: false,
        isUploadingToFirestore: false,
        loaded: false,
        orientation: null,
        sizeBytes: null,
        sizeFormatted: null,
        width: null,
      },
    };
  },
  created() {
    this.avatarToUse = this.getAvatarToUse;
    this.dataThemeBeforeEditing = document.documentElement.getAttribute('data-theme');
    this.firstName = this.getFirstName;
    this.gamerTag = this.getGamerTag;
    this.gamerTagMaxLength = GAMERTAG_MAX_LENGTH;
    this.lastName = this.getLastName;
    this.yearBorn = this.getYearBorn;
    this.country = this.getCountry;
    this.overrideOsDarkMode = this.getOverrideOsDarkMode;
    this.playSoundEffects = this.getPlaySoundEffects;
    this.prefersColorScheme = this.getPrefersColorScheme;
    this.customAvatarFilename = this.getCustomAvatarFilename;
    this.customAvatarOrientation = this.getCustomAvatarOrientation;
    this.customAvatarOriginalFilesizeKb = this.getCustomAvatarOriginalFilesizeKb;
    this.customAvatarOriginalHeight = this.getCustomAvatarOriginalHeight;
    this.customAvatarOriginalWidth = this.getCustomAvatarOriginalWidth;
    this.customAvatarUrl = this.getCustomAvatarUrl;
    this.userIdShort = this.getUserIdShort;
    this.userId = this.getUserId;
    logUserActivity(VIEWED_PROFILE);

    const now = new Date();
    let minYear = now.getFullYear() - 100;
    let maxYear = now.getFullYear() - 10;
    for (let i = maxYear; i >= minYear; i--) {
      this.years.push(i);
    }
  },
  mounted() {},
  methods: {
    updateUserProfile() {
      if (this.submittingForm) return;
      this.userSavedChanges = true;
      this.submittingForm = true;
      this.$store.dispatch('userVuexModule/SET_USER_PROFILE_DATA_ACTION', {
        avatarToUse: this.avatarToUse,
        firstName: this.firstName,
        gamerTag: this.gamerTag,
        lastName: this.lastName,
        yearBorn: this.yearBorn,
        country: this.country,
        overrideOsDarkMode: this.overrideOsDarkMode,
        playSoundEffects: this.playSoundEffects,
        prefersColorScheme: this.prefersColorScheme,
        customAvatarFilename: this.customAvatarFilename || '',
        customAvatarOrientation: this.customAvatarOrientation,
        customAvatarOriginalFilesizeKb: this.uploadedCustomAvatarImage.sizeBytes
          ? Math.round(this.uploadedCustomAvatarImage.sizeBytes / 1000)
          : this.customAvatarOriginalFilesizeKb,
        customAvatarOriginalHeight: this.uploadedCustomAvatarImage.height ? this.uploadedCustomAvatarImage.height : this.customAvatarOriginalHeight,
        customAvatarOriginalWidth: this.uploadedCustomAvatarImage.width ? this.uploadedCustomAvatarImage.width : this.customAvatarOriginalWidth,
        customAvatarUrl: this.customAvatarUrl,
      });
      this.$store.dispatch('userVuexModule/SET_PREFERS_COLOR_SCHEME_TEMP_ACTION', {
        prefersColorSchemeTemp: null,
      });
      localStorage.setItem('overrideOsDarkMode', this.overrideOsDarkMode);
      this.dataThemeBeforeEditing = this.prefersColorScheme;
      if (this.overrideOsDarkMode) {
        localStorage.setItem('prefersColorScheme', this.prefersColorScheme);
        document.documentElement.setAttribute('data-theme', this.prefersColorScheme);
      } else {
        localStorage.removeItem('prefersColorScheme');
        if (window.matchMedia) {
          if (window.matchMedia('screen and (prefers-color-scheme: dark)').matches) {
            document.documentElement.setAttribute('data-theme', COLOR_SCHEMES.DARK.CONSTANT);
          } else {
            document.documentElement.setAttribute('data-theme', COLOR_SCHEMES.LIGHT.CONSTANT);
          }
        }
      }
      this.$snotify.success('Profile updated', '', NOTIFICATION_SETTINGS).on('destroyed', () => {
        this.submittingForm = false;
        this.$router.push('/dashboard');
      });
    },
    readImageData(evt) {
      let file = evt.target.files[0];
      if (!file || file.type.indexOf('image/') !== 0) return;
      this.uploadedCustomAvatarImage.isReadingFile = true;
      this.uploadedCustomAvatarImage.sizeBytes = file.size;
      this.uploadedCustomAvatarImage.sizeFormatted = bytesToSize(file.size);
      if (file.size < MAXIMUM_CUSTOM_AVATAR_UPLOAD_BYTES) {
        this.uploadedCustomAvatarImage.extension = file.type.split('/')[1];
        if (this.uploadedCustomAvatarImage.extension === 'jpeg') this.uploadedCustomAvatarImage.extension = 'jpg';
        let fileReader = new FileReader();
        fileReader.readAsDataURL(file);
        fileReader.onload = evt => {
          let img = new Image();
          img.onload = () => {
            this.uploadedCustomAvatarImage.width = img.width;
            this.uploadedCustomAvatarImage.height = img.height;
            this.uploadedCustomAvatarImage.loaded = true;
            this.uploadedCustomAvatarImage.isReadingFile = false;
            this.uploadedCustomAvatarImage.orientation = getOrientationFromDimensions(img.width, img.height);
            this.uploadImageToFirestore(file);
          };
          img.src = evt.target.result;
        };
        fileReader.onerror = evt => {
          // console.error(evt);
        };
      } else {
        this.uploadedCustomAvatarImage.isReadingFile = false;
        alert(`The image must be less than ${bytesToSize(MAXIMUM_CUSTOM_AVATAR_UPLOAD_BYTES)}`);
      }
    },
    uploadImageToFirestore(file) {
      const newFirestoreFilename = `${this.getUserIdShort}_${getFormattedTimestamp()}.${this.uploadedCustomAvatarImage.extension}`;
      const firestoreRef = firebase.storage().ref(`${FIREBASE_STORAGE_USER_AVATARS_FOLDER}/${newFirestoreFilename}`);
      const firestorePutTask = firestoreRef.put(file);
      this.uploadedCustomAvatarImage.isUploadingToFirestore = true;
      firestorePutTask.on(
        'state_changed',
        snapshot => {
          this.imageUploadPercent = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        },
        err => {
          this.$snotify.error('Error uploading image', '', NOTIFICATION_SETTINGS);
        },
        () => {
          this.customAvatarFilename = newFirestoreFilename;
          this.getCustomAvatarUrlFromFirebaseStorage();
          this.uploadedCustomAvatarImage.isUploadingToFirestore = false;
          this.customAvatarOrientation = this.uploadedCustomAvatarImage.orientation;
        }
      );
    },
    getCustomAvatarUrlFromFirebaseStorage() {
      const storageRef = firebase
        .storage()
        .ref(`${FIREBASE_STORAGE_USER_AVATARS_FOLDER}/${this.customAvatarFilename}`)
        .getDownloadURL()
        .then(url => {
          this.customAvatarUrl = url;
        })
        .catch(error => {
          this.$snotify.error('Error getting custom avatar URL', '', NOTIFICATION_SETTINGS);
        });
    },
    gamerTagInput({type, target}) {
      this.gamerTag = target.value.replace(/[^a-z0-9_]/gi, '');
    },
    firstNameInput({type, target}) {
      this.firstName = capitalize(target.value.replace(/[^a-z'-]/gi, ''));
    },
    lastNameInput({type, target}) {
      this.lastName = capitalize(target.value.replace(/[^a-z'-]/gi, ''));
    },
    yearBornInput({type, target}) {
      this.yearBorn = target.value.replace(/[^0-9]/gi, '');
    },
    onOverrideOsDarkModeChange({type, target}) {
      if (this.overrideOsDarkMode) {
        if (window.matchMedia) {
          if (window.matchMedia('screen and (prefers-color-scheme: dark)').matches) {
            this.prefersColorScheme = COLOR_SCHEMES.LIGHT.CONSTANT;
          } else {
            this.prefersColorScheme = COLOR_SCHEMES.DARK.CONSTANT;
          }
        }
        document.documentElement.setAttribute('data-theme', this.prefersColorScheme);
      } else if (window.matchMedia) {
        if (window.matchMedia('screen and (prefers-color-scheme: dark)').matches) {
          document.documentElement.setAttribute('data-theme', COLOR_SCHEMES.DARK.CONSTANT);
        } else {
          document.documentElement.setAttribute('data-theme', COLOR_SCHEMES.LIGHT.CONSTANT);
        }
      }
    },
    onPrefersColorSchemeChange({type, target}) {
      document.documentElement.setAttribute('data-theme', this.prefersColorScheme);
      this.$store.dispatch('userVuexModule/SET_PREFERS_COLOR_SCHEME_TEMP_ACTION', {
        prefersColorSchemeTemp: this.prefersColorScheme,
      });
    },
    onAvatarToUseChange({type, target}) {
      // console.log('onAvatarToUseChange');
      // document.documentElement.setAttribute('data-theme', this.prefersColorScheme);
    },
    getCustomAvatarImageModifierClass() {
      let orientation = this.getCustomAvatarOrientation.toLowerCase();
      return `custom-avatar-image--${orientation} ${orientation}`;
    },
    onCancel() {
      this.$router.go(-1);
    },
    onUserIdClick() {
      const el = document.createElement('textarea');
      el.value = this.getUserId;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      this.$snotify.info('User ID copied to clipboard', '', NOTIFICATION_SETTINGS);
    },
  },
  computed: {
    ...userVuexModule.mapGetters([
      'getAvatarToUse',
      'getCountry',
      'getCustomAvatarFilename',
      'getCustomAvatarOrientation',
      'getCustomAvatarOriginalFilesizeKb',
      'getCustomAvatarOriginalHeight',
      'getCustomAvatarOriginalWidth',
      'getCustomAvatarUrl',
      'getEmail',
      'getFirstName',
      'getGamerTag',
      'getLastName',
      'getOverrideOsDarkMode',
      'getPlaySoundEffects',
      'getPrefersColorScheme',
      'getUserId',
      'getUserIdShort',
      'getYearBorn',
    ]),
    hash: function() {
      if (!this.getEmail) return;
      return md5(this.getEmail.trim());
    },
    defaultAvatarUrl: function() {
      const images = require.context('../assets/', false, /\.png$/);
      return images(`./default-avatar-${this.prefersColorScheme.toLowerCase()}.png`);
    },
    showCustomAvatarLoader: function() {
      return this.uploadedCustomAvatarImage.isReadingFile || this.uploadedCustomAvatarImage.isUploadingToFirestore;
    },
    resizedCustomAvatarUrl: function() {
      if (this.customAvatarUrl === this.getCustomAvatarUrl) {
        return resizedCustomAvatarUrl(this.customAvatarUrl, FIREBASE_STORAGE_RESIZED_IMAGE_SUFFIXES.SIZE_400.SUFFIX);
      } else {
        return this.customAvatarUrl;
      }
    },
  },
  beforeDestroy() {
    if (!this.userSavedChanges) {
      document.documentElement.setAttribute('data-theme', this.dataThemeBeforeEditing);
    }
    this.$store.dispatch('userVuexModule/SET_PREFERS_COLOR_SCHEME_TEMP_ACTION', {
      prefersColorSchemeTemp: null,
    });
  },
};
</script>

<style lang="scss" scoped>
.input {
  &--userid {
    cursor: pointer;
  }
}

.default-avatar-image,
.gravatar-image {
  border-radius: 50%;
  height: 240px;
  margin: 0 auto;
  width: 240px;
}

.gravatar-wrapper {
  border-radius: 50%;
  height: 240px;
  margin: 0 auto;
  overflow: hidden;
  position: relative;
  width: 240px;

  img {
    border-radius: 0;
  }

  span {
    align-items: center;
    background-color: hsla(0, 0%, 0%, 0.66);
    background-color: var(--color-gravatar-upload-alpha);
    bottom: 0;
    display: block;
    display: flex;
    height: 4rem;
    justify-content: center;
    position: absolute;
    width: 100%;
  }

  a {
    color: var(--color-form-element-text);
    font-weight: 400;
    padding: 0.2rem 0.5rem;
  }
}

.custom-avatar-wrapper {
  background-color: var(--color-form-element-background);
  border-radius: 50%;
  height: 240px;
  margin: 0 auto;
  overflow: hidden;
  position: relative;
  width: 240px;

  &::before {
    color: var(--color-form-element-text);
    content: '?';
    display: block;
    font-size: 2rem;
    font-weight: 400;
    left: 50%;
    opacity: 0.2;
    position: absolute;
    text-align: center;
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: 1;
  }

  .loader {
    display: block;
    left: 50%;
    position: absolute;
    top: 0;
    transform: translate(-50%, 1.5rem) scale(0.5);
    z-index: 4;
  }

  img {
    border-radius: 0;
    max-width: none;
    /* object-fit: cover; */
    position: relative;
    z-index: 2;

    &.landscape {
      width: auto;
      height: 100%;
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
    }

    &.portrait {
      width: 100%;
      height: auto;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
    }

    &.square {
      width: 100%;
      height: 100%;
    }
  }

  div {
    align-items: center;
    background-color: var(--color-custom-avatar-upload-alpha);
    bottom: 0;
    display: block;
    display: flex;
    height: 4rem;
    justify-content: center;
    position: absolute;
    width: 100%;
    z-index: 3;

    span {
      color: var(--color-form-element-text);
      font-weight: 400;
      pointer-events: none;
      text-decoration: underline;
      user-select: none;
    }

    input {
      border-radius: 0;
      bottom: 0;
      cursor: pointer;
      display: block;
      height: 100%;
      left: 0;
      opacity: 0;
      position: absolute;
      right: 0;
      top: 0;
      width: 100%;
    }

    progress {
      height: 1px;
      margin: 0;
      padding: 0;
      position: absolute;
      top: 0;
      width: 100%;
    }
  }
}

.cancel-btn {
  margin-top: 2rem;
}
</style>
